import React from 'react';
import { VehicleModel, VehicleModelDataSource } from '../../types';
import { useMasterData } from '../../hooks/useMasterData';
import {
  XMarkIcon,
  TagIcon,
  TruckIcon,
  BuildingOfficeIcon,
  ClipboardDocumentListIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { Button } from '../ui';
import {
  getPowertrainLabel,
  getVehicleTagLabels,
  getDataSourceLabel,
} from '../../utils/vehicleModelUtils';

interface VehicleModelDetailProps {
  vehicleModel: VehicleModel;
  onClose: () => void;
  onEdit?: (vehicleModel: VehicleModel) => void;
  onDelete?: (modelId: string, modelName: string) => void;
  isModal?: boolean;
}

export const VehicleModelDetail: React.FC<VehicleModelDetailProps> = ({
  vehicleModel,
  onClose,
  onEdit,
  onDelete,
  isModal = true,
}) => {
  const { manufacturers, vehicleTypes, getVehicleModelActiveUsageCount } = useMasterData();

  const manufacturer = manufacturers.find(m => m.id === vehicleModel.manufacturerId);
  const vehicleType = vehicleTypes.find(vt => vt.id === vehicleModel.vehicleTypeId);
  const activeUsageCount = getVehicleModelActiveUsageCount(vehicleModel.id);

  const getDataSourceDisplay = (dataSource: VehicleModelDataSource) => {
    return dataSource === VehicleModelDataSource.RAKUHAI_PROVIDED
      ? {
          icon: '🏢',
          text: getDataSourceLabel(dataSource),
          color: 'text-blue-600 dark:text-blue-400',
        }
      : {
          icon: '✏️',
          text: getDataSourceLabel(dataSource),
          color: 'text-green-600 dark:text-green-400',
        };
  };

  const dataSourceInfo = getDataSourceDisplay(vehicleModel.dataSource);

  const handleEditClick = () => {
    if (onEdit) {
      onEdit(vehicleModel);
    }
  };

  const handleDeleteClick = () => {
    if (onDelete) {
      onDelete(vehicleModel.id, vehicleModel.name);
    }
  };

  const DetailContent = () => (
    <div className='space-y-6'>
      {/* ヘッダー */}
      <div className='flex items-start justify-between'>
        <div className='flex-1 min-w-0'>
          <div className='flex items-center space-x-3 mb-2'>
            <TruckIcon className='h-6 w-6 text-blue-600 dark:text-blue-400' />
            <h2 className='text-xl sm:text-2xl font-bold text-gray-900 dark:text-white truncate'>
              {manufacturer?.name || '不明'} {vehicleModel.name}
            </h2>
          </div>
          <div className='flex items-center space-x-2'>
            <span className={`text-sm font-medium ${dataSourceInfo.color}`}>
              {dataSourceInfo.icon} {dataSourceInfo.text}
            </span>
          </div>
        </div>
        {isModal && (
          <button
            onClick={onClose}
            className='p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700'
            aria-label='詳細を閉じる'
          >
            <XMarkIcon className='h-5 w-5' />
          </button>
        )}
      </div>

      {/* 基本情報 */}
      <div className='bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-4'>
        <h3 className='text-lg font-semibold text-gray-900 dark:text-white flex items-center'>
          <BuildingOfficeIcon className='h-5 w-5 mr-2' />
          基本情報
        </h3>

        <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
          <div>
            <label className='block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1'>
              メーカー
            </label>
            <p className='text-sm text-gray-900 dark:text-white'>{manufacturer?.name || '不明'}</p>
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1'>
              モデル名
            </label>
            <p className='text-sm text-gray-900 dark:text-white font-medium'>{vehicleModel.name}</p>
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1'>
              車種タイプ
            </label>
            <span className='inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'>
              {vehicleType?.name || '不明'}
            </span>
          </div>
        </div>
      </div>

      {/* 使用状況 */}
      <div className='bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-4'>
        <h3 className='text-lg font-semibold text-gray-900 dark:text-white flex items-center'>
          <ClipboardDocumentListIcon className='h-5 w-5 mr-2' />
          使用状況
        </h3>

        <div className='space-y-3'>
          <div className='flex items-center space-x-2'>
            <span className='text-sm font-medium text-gray-600 dark:text-gray-300'>
              稼働中案件数:
            </span>
            <span className='text-sm font-semibold text-gray-900 dark:text-white'>
              {activeUsageCount} 件
            </span>
          </div>

          {activeUsageCount === 0 && (
            <div className='flex items-center'>
              <span className='inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-600 dark:text-gray-300'>
                未使用
              </span>
              <span className='ml-2 text-xs text-gray-500 dark:text-gray-400'>
                この車両モデルは現在使用されていません
              </span>
            </div>
          )}

          {activeUsageCount > 0 && (
            <>
              <div className='flex items-center'>
                <span className='inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'>
                  使用中
                </span>
                <span className='ml-2 text-xs text-gray-500 dark:text-gray-400'>
                  この車両モデルは現在使用中です
                </span>
              </div>
              <div className='mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md'>
                <p className='text-xs text-yellow-800 dark:text-yellow-200'>
                  ⚠️ この車両モデルは稼働中の案件で使用されているため、削除できません。
                  削除するには、関連する案件を完了させるか、別の車両モデルに変更してください。
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* 詳細仕様 */}
      <div className='bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-4'>
        <h3 className='text-lg font-semibold text-gray-900 dark:text-white flex items-center'>
          <TagIcon className='h-5 w-5 mr-2' />
          詳細仕様
        </h3>

        <div className='space-y-3'>
          <div>
            <label className='block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2'>
              パワートレイン
            </label>
            {vehicleModel.powertrain ? (
              <span className='inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'>
                {getPowertrainLabel(vehicleModel.powertrain || null)}
              </span>
            ) : (
              <span className='text-sm text-gray-500 dark:text-gray-400'>未設定</span>
            )}
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2'>
              車両タグ
            </label>
            {vehicleModel.vehicleTags && vehicleModel.vehicleTags.length > 0 ? (
              <div className='flex flex-wrap gap-2'>
                {getVehicleTagLabels(vehicleModel.vehicleTags).map((tagLabel, index) => (
                  <span
                    key={index}
                    className='inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                  >
                    {tagLabel}
                  </span>
                ))}
              </div>
            ) : (
              <span className='text-sm text-gray-500 dark:text-gray-400'>なし</span>
            )}
          </div>
        </div>
      </div>

      {/* 削除警告 */}
      {activeUsageCount > 0 && (
        <div className='p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md'>
          <p className='text-xs text-yellow-800 dark:text-yellow-200'>
            ⚠️
            この車両モデルは稼働中の案件で使用されているため削除できません。削除するには、関連する案件を完了させるか、別の車両モデルに変更してください。
          </p>
        </div>
      )}

      {/* アクション */}
      <div className='flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-600'>
        {/* 削除ボタン（未使用の場合のみ表示） */}
        {activeUsageCount === 0 && onDelete && (
          <Button onClick={handleDeleteClick} variant='danger' size='md'>
            <TrashIcon className='h-4 w-4 mr-2' />
            削除
          </Button>
        )}

        {/* 編集ボタン（独自登録の場合のみ表示） */}
        {vehicleModel.dataSource === VehicleModelDataSource.CUSTOM && onEdit && (
          <Button onClick={handleEditClick} variant='primary' size='md'>
            <PencilIcon className='h-4 w-4 mr-2' />
            編集
          </Button>
        )}

        {!isModal ? (
          <Button onClick={onClose} variant='secondary' size='md'>
            戻る
          </Button>
        ) : null}
      </div>
    </div>
  );

  if (isModal) {
    return (
      <div className='fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50'>
        <div
          role='dialog'
          aria-label='車両モデル詳細'
          className='bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto'
        >
          <div className='p-6'>
            <DetailContent />
          </div>
        </div>
      </div>
    );
  }

  return <DetailContent />;
};
